import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { isMobile } from "react-device-detect";

import { IRootState } from "../store";

type State = {
  isNavbarClosed: boolean;
  isSearching: boolean;
  isCurrentlyImporting: boolean;
  /**
   * Bumped when the import overlay's Done is pressed. The overlay is global
   * (ModalControllerV2) so it can be raised from any CTA, which means it can no
   * longer hand a callback back to whoever started the import — a timestamp in
   * the store is the serialisable way for a page to notice its import ended.
   */
  lastImportFinishedAt: number;
  showFooter: boolean;
  isCurrentlyOnboarding: boolean;
  topToPage: {
    value: number;
    tailwindString: string;
    closedTopBar: boolean;
  };
  isNavbarDrawerOpen: boolean;
  isShowingScrolledNavbar: boolean;
  touchBarMenuZIndex: string;
  /** A page with its own sticky top row opts out of the floating burger. */
  isFloatingMenuHidden: boolean;
  /** The compare sheet, so a page can open it with players it just staged. */
  isCompareSheetOpen: boolean;
  /**
   * Closing the sheet also leaves compare mode (clears the staged players).
   * Set by places that open a one-off comparison, like My Club's
   * recommendations, so closing it doesn't leave a compare pill behind.
   */
  compareExitOnClose: boolean;
};

const initialState: State = {
  isNavbarClosed: isMobile,
  isSearching: false,
  isCurrentlyImporting: false,
  lastImportFinishedAt: 0,
  showFooter: true,
  isCurrentlyOnboarding: false,
  topToPage: {
    value: 48,
    tailwindString: "top-12",
    closedTopBar: false,
  },
  isShowingScrolledNavbar: false,
  isNavbarDrawerOpen: false,
  touchBarMenuZIndex: "z-11",
  isFloatingMenuHidden: false,
  isCompareSheetOpen: false,
  compareExitOnClose: false,
};

const globalStateSlice = createSlice({
  name: "globalState",
  initialState,
  reducers: {
    setCompareSheetOpen(state, action: PayloadAction<boolean>) {
      state.isCompareSheetOpen = action.payload;
      if (!action.payload) state.compareExitOnClose = false;
    },
    /** Opens the sheet; `exitOnClose` leaves compare mode when it closes. */
    openCompareSheet(
      state,
      action: PayloadAction<{ exitOnClose?: boolean } | undefined>,
    ) {
      state.isCompareSheetOpen = true;
      state.compareExitOnClose = !!action.payload?.exitOnClose;
    },
    toggleSearching(state, action: PayloadAction<boolean>) {
      state.isSearching = action.payload;
    },
    toggleNavbarOpen(state, action: PayloadAction<boolean>) {
      state.isNavbarClosed = action.payload;
    },
    setIsCurrentlyImporting(state, action: PayloadAction<boolean>) {
      state.isCurrentlyImporting = action.payload;
    },
    markImportFinished(state, action: PayloadAction<number>) {
      state.isCurrentlyImporting = false;
      state.lastImportFinishedAt = action.payload;
    },
    setShowFooter(state, action: PayloadAction<boolean>) {
      state.showFooter = action.payload;
    },
    setTopToPage(
      state,
      action: PayloadAction<{
        value: number;
        tailwindString: string;
        closedTopBar: boolean;
      }>,
    ) {
      state.topToPage = action.payload;
    },
    setIsCurrentlyOnboarding(state, action: PayloadAction<boolean>) {
      state.isCurrentlyOnboarding = action.payload;
    },
    handleOpenDrawer(state) {
      state.isNavbarDrawerOpen = true;
    },
    handleCloseDrawer(state) {
      state.isNavbarDrawerOpen = false;
    },
    setIsShowingScrolledNavbar(state, action: PayloadAction<boolean>) {
      state.isShowingScrolledNavbar = action.payload;
    },
    setTouchBarMenuZIndex(state, action: PayloadAction<string>) {
      state.touchBarMenuZIndex = action.payload;
    },
    cleanTouchBarMenuZIndex(state) {
      state.touchBarMenuZIndex = "z-11";
    },
    setIsFloatingMenuHidden(state, action: PayloadAction<boolean>) {
      state.isFloatingMenuHidden = action.payload;
    },
  },
});

export const globalStateSelector = (state: IRootState) =>
  state.globalStateReducer;

export const topToPageSelector = (state: IRootState) =>
  state.globalStateReducer.topToPage;

export const {
  toggleNavbarOpen,
  toggleSearching,
  setIsCurrentlyImporting,
  markImportFinished,
  setShowFooter,
  setTopToPage,
  setIsCurrentlyOnboarding,
  handleOpenDrawer,
  handleCloseDrawer,
  setIsShowingScrolledNavbar,
  setTouchBarMenuZIndex,
  cleanTouchBarMenuZIndex,
  setIsFloatingMenuHidden,
  setCompareSheetOpen,
  openCompareSheet,
} = globalStateSlice.actions;
export default globalStateSlice.reducer;
